"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Ce qu'on construit avec Node.js. Couleurs écrites en toutes lettres pour que
// Tailwind les génère bien (pas de classes dynamiques tronquées).
const DOMAINS = [
    {
        icon: "server-bold-duotone",
        titleKey: "Node_everywhere_server_title",
        descKey: "Node_everywhere_server_desc",
        bg: "bg-sky-500/15",
        ring: "ring-sky-400/30",
        text: "text-sky-300",
    },
    {
        icon: "plug-circle-bold-duotone",
        titleKey: "Node_everywhere_api_title",
        descKey: "Node_everywhere_api_desc",
        bg: "bg-emerald-500/15",
        ring: "ring-emerald-400/30",
        text: "text-emerald-300",
    },
    {
        icon: "chat-round-dots-bold-duotone",
        titleKey: "Node_everywhere_realtime_title",
        descKey: "Node_everywhere_realtime_desc",
        bg: "bg-violet-500/15",
        ring: "ring-violet-400/30",
        text: "text-violet-300",
    },
    {
        icon: "command-bold-duotone",
        titleKey: "Node_everywhere_cli_title",
        descKey: "Node_everywhere_cli_desc",
        bg: "bg-amber-500/15",
        ring: "ring-amber-400/30",
        text: "text-amber-300",
    },
    {
        icon: "widget-bold-duotone",
        titleKey: "Node_everywhere_micro_title",
        descKey: "Node_everywhere_micro_desc",
        bg: "bg-rose-500/15",
        ring: "ring-rose-400/30",
        text: "text-rose-300",
    },
    {
        icon: "videocamera-record-bold-duotone",
        titleKey: "Node_everywhere_stream_title",
        descKey: "Node_everywhere_stream_desc",
        bg: "bg-cyan-500/15",
        ring: "ring-cyan-400/30",
        text: "text-cyan-300",
    },
    {
        icon: "refresh-bold-duotone",
        titleKey: "Node_everywhere_automation_title",
        descKey: "Node_everywhere_automation_desc",
        bg: "bg-indigo-500/15",
        ring: "ring-indigo-400/30",
        text: "text-indigo-300",
    },
    {
        icon: "cloud-bold-duotone",
        titleKey: "Node_everywhere_cloud_title",
        descKey: "Node_everywhere_cloud_desc",
        bg: "bg-orange-500/15",
        ring: "ring-orange-400/30",
        text: "text-orange-300",
    },
];

// Géants du web qui font réellement tourner Node.js (vrais logos).
const COMPANIES = [
    { name: "Netflix", logo: "logos:netflix-icon" },
    { name: "PayPal", logo: "logos:paypal" },
    { name: "LinkedIn", logo: "logos:linkedin-icon" },
    { name: "Uber", logo: "simple-icons:uber" },
    { name: "NASA", logo: "simple-icons:nasa" },
    { name: "Walmart", logo: "simple-icons:walmart" },
    { name: "eBay", logo: "simple-icons:ebay" },
    { name: "Trello", logo: "logos:trello" },
];

const gridContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const cardItem = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.45, ease: "easeOut" },
    },
};

export default function NodeEverywhere() {
    const t = useTranslations();

    return (
        <section id="nodejs-everywhere" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Node_everywhere_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Node_everywhere_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-8 sm:p-12">
                {/* Glows (teinte Node) */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-green-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Eyebrow */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-green-500/15 ring-1 ring-green-400/30 px-3 py-1 mb-4">
                        <SolarIcon
                            name="global-bold-duotone"
                            className="w-3.5 h-3.5 text-green-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-green-300">
                            {t("Node_everywhere_eyebrow")}
                        </span>
                    </div>

                    {/* Headline */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.12] tracking-tight mb-8 max-w-[640px]">
                        {t.rich("Node_everywhere_headline", {
                            highlight: (chunks) => (
                                <span className="bg-gradient-to-r from-green-300 to-emerald-200 bg-clip-text text-transparent">
                                    {chunks}
                                </span>
                            ),
                        })}
                    </h3>

                    {/* Grille des usages */}
                    <motion.div
                        variants={gridContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-80px" }}
                        className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5"
                    >
                        {DOMAINS.map((domain) => (
                            <motion.div
                                key={domain.titleKey}
                                variants={cardItem}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition-colors duration-200"
                            >
                                <div
                                    className={`w-11 h-11 rounded-xl ${domain.bg} ring-1 ${domain.ring} flex items-center justify-center mb-4`}
                                >
                                    <SolarIcon
                                        name={domain.icon}
                                        className={`w-5 h-5 ${domain.text}`}
                                    />
                                </div>
                                <h4 className="text-base font-semibold text-white mb-1 leading-tight">
                                    {t(domain.titleKey)}
                                </h4>
                                <p className="text-sm text-gray-300/80 leading-relaxed mb-0">
                                    {t(domain.descKey)}
                                </p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Preuve sociale : géants du web + logos */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                        className="mt-8 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-5"
                    >
                        <div className="flex items-start gap-3">
                            <SolarIcon
                                name="verified-check-bold"
                                className="w-5 h-5 text-green-300 shrink-0 mt-0.5"
                            />
                            <p className="text-sm text-gray-300/90 leading-relaxed mb-0">
                                {t.rich("Node_everywhere_social_proof", {
                                    b: (chunks) => (
                                        <strong className="text-white font-semibold">
                                            {chunks}
                                        </strong>
                                    ),
                                })}
                            </p>
                        </div>
                        <div className="mt-4 flex flex-wrap items-center gap-2.5">
                            {COMPANIES.map((company) => (
                                <span
                                    key={company.name}
                                    title={company.name}
                                    className="inline-flex items-center justify-center h-9 w-12 rounded-md bg-white ring-1 ring-black/5"
                                >
                                    <Icon
                                        icon={company.logo}
                                        className="w-[18px] h-[18px]"
                                        aria-label={company.name}
                                    />
                                </span>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
